import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Stack,
  Container,
  Typography,
  TextField,
  Button,
  Link,
  Card,
  Grid,
} from '@mui/material';
import Page from '../../components/Page';
import { LoadingButton } from '@mui/lab';
import { useAppDispatch, useAppSelector } from '../../store';
import { useFormik } from 'formik';
import { authenticateWhatsAppOtpUser } from '../../store/auth/api';
import { getItem, setItem } from '../../utils/localStorage';
import { login } from '../../store/auth/authSlice';
import { reset as resetWhatsAppLoginOtp } from '../../store/whatsappLogin/whatsappLoginSlice';
import { createWhatsappLogin } from '../../store/whatsappLogin/api';
import { toast } from 'react-toastify';
import { removeItem } from '../../utils/localStorage';
import { IsNullOrEmpty } from '../../utils/valid';
import { useNavigate } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import { messaging } from '../../init-fcm';
import { getToken } from 'firebase/messaging';
import { getOrRegisterServiceWorker } from '../../index';
import { findNotification } from '../../store/notification/api';
import { NotificationModuleType } from '../../enum';

const RootStyle = styled(Page)(({ theme }) => ({
  [theme.breakpoints.up('md')]: {
    display: 'flex',
  },
}));

const ContentStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0),
}));

export interface Values {
  id: string;
  email: string;
  otp: string;
  password: string;
}

const Index: React.FC = () => {
  const Ref = useRef<any>(null);
  const [showOTP, setOTP] = useState<string>('');
  // const { id } = useAppSelector((state) => state.auth.user);
  const { data: WhatsappLoginOtpData } = useAppSelector(
    (state) => state.whatsappLogin
  );
  const [fcmToken, setFCMToken] = useState<string>('');
  const [timer, setTimer] = useState('00:00');
  const dispatch = useAppDispatch();
  const userMobileNumber = getItem('imobile');
  const userCountryCode = getItem('icode');
  const navigation = useNavigate();

  useEffect(() => {
    if (IsNullOrEmpty(userMobileNumber)) {
      toast.error('Please add your mobile number first');
      navigation('/login');
    }
  }, []);

  useEffect(() => {
    resetTimer();
  }, [WhatsappLoginOtpData?.requestId]);

  // FCM Token useEffect
  useEffect(() => {
    const firebaseNotificationFunction = async () => {
      if (!messaging) {
        console.info(
          'Firebase messaging is not available in this environment.'
        );
        return;
      }

      try {
        const messagingResolve = await messaging;

        if (messagingResolve) {
          try {
            const serviceWorkerRegistration: any =
              await getOrRegisterServiceWorker();
            const permission = await Notification.requestPermission();

            if (permission === 'granted') {
              try {
                const currentToken = await getToken(messagingResolve, {
                  vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY,
                  serviceWorkerRegistration,
                });

                if (currentToken) {
                  setFCMToken(currentToken);
                } else {
                  console.error(
                    'No registration token available. Request permission to generate one.'
                  );
                }
              } catch (err) {
                console.error(
                  'An error occurred while retrieving token. ',
                  err
                );
              }
            } else {
              console.error('Notification permission not granted.');
            }
          } catch (error) {
            console.error('Service worker registration failed:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing Firebase messaging:', error);
      }
    };

    firebaseNotificationFunction();
  }, []);

  const handleInput = (value: string) => {
    setOTP(value);
  };

  const isOtpValid = (otp: string) => {
    const otpRegex = /^[0-9]{1,4}$/;
    return otpRegex.test(otp);
  };

  const { handleSubmit, resetForm, values, setFieldValue, isSubmitting } =
    useFormik({
      initialValues: {
        token: '',
        otp: '',
        strategy: 'otp',
        requestId: WhatsappLoginOtpData?.requestId,
      },
      onSubmit: async () => {
        try {
          if (values.requestId) {
            if (fcmToken) {
              values.token = fcmToken;
            }
            values.otp = showOTP;
            const res = await dispatch(
              authenticateWhatsAppOtpUser(values)
            ).unwrap();
            if (res?.accessToken) {
              await dispatch(
                findNotification({
                  isRead: false,
                  isExport: true,
                  moduleType: NotificationModuleType.order,
                  receiverUserId: res?.user?.id,
                })
              );
              setItem('0verifyOtp', false);
              setItem('info', res?.user);
              dispatch(
                login({ accessToken: res?.accessToken, user: res?.user })
              );
              resetForm();
              dispatch(resetWhatsAppLoginOtp());
              removeItem('imobile');
              removeItem('icode');
            }
          } else {
            toast.error('Invalid OTP');
          }
        } catch (error: any) {}
      },
    });

  useEffect(() => {
    setFieldValue('requestId', WhatsappLoginOtpData?.requestId);
  }, [WhatsappLoginOtpData?.requestId]);

  const getTimeRemaining = (e: string) => {
    const total = Date.parse(e) - Date.now();
    const seconds = Math.floor((total / 1000) % 60);
    const minutes = Math.floor((total / 1000 / 60) % 60);
    const hours = Math.floor((total / 1000 / 60 / 60) % 24);
    return {
      total,
      hours,
      minutes,
      seconds,
    };
  };

  const startTimer = (e: string) => {
    const { total, minutes, seconds } = getTimeRemaining(e);
    if (total >= 0) {
      setTimer(
        (minutes > 9 ? minutes : '0' + minutes) +
          ':' +
          (seconds > 9 ? seconds : '0' + seconds)
      );
    }
  };

  const clearTimer = (e: string) => {
    setTimer('00:59');
    if (Ref.current) clearInterval(Ref.current);
    const id = setInterval(() => {
      startTimer(e);
    }, 1000);
    Ref.current = id;
  };

  const resetTimer = () => {
    clearTimer(getDeadTime());
  };

  const getDeadTime = () => {
    const deadline = new Date();
    deadline.setSeconds(deadline.getSeconds() + 59);
    return deadline.toString();
  };

  const resendCode = async (deliveryMethod: 'whatsapp' | 'sms' = 'whatsapp') => {
    if (timer === '00:00') {
      resetTimer();
    }
    const whatsappLoginValues = {
      mobileNumber: userMobileNumber,
      countryCallingCode: userCountryCode,
      deliveryMethod,
    };
    try {
      await dispatch(createWhatsappLogin(whatsappLoginValues)).unwrap();
      toast.success(`OTP sent via ${deliveryMethod === 'sms' ? 'SMS' : 'WhatsApp'}`);
    } catch (error) {
      // Error is already handled by toastHandler in the action
    }
  };

  return (
    <RootStyle title={'Verify Whatsapp OTP'}>
      <Container maxWidth='sm'>
        <ContentStyle>
          <Card
            sx={{
              p: { xs: '50px 20px 50px 20px', sm: '50px 30px 50px 30px' },
              boxShadow: 'rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
            }}
          >
            <Stack sx={{ mb: 2 }}>
              <Typography
                variant='h4'
                gutterBottom
              >
                Verify Whatsapp OTP
              </Typography>
            </Stack>
            <form
              autoComplete='off'
              noValidate
              onSubmit={handleSubmit}
            >
              <Stack spacing={3}>
                <Grid
                  container
                  spacing={3}
                  sx={{ width: '100%' }}
                >
                  <Grid
                    // item
                    xs={3}
                    sm={3}
                    md={3}
                    lg={3}
                  >
                    <TextField
                      fullWidth
                      autoComplete='code'
                      label={'Code'}
                      name='countryCallingCode'
                      disabled={true}
                      value={userCountryCode}
                    />
                  </Grid>

                  <Grid
                    // item
                    xs={9}
                    sm={9}
                    md={9}
                    lg={9}
                    pl={2}
                  >
                    <TextField
                      fullWidth
                      autoComplete='mob'
                      label={'Mobile Number'}
                      disabled={true}
                      value={userMobileNumber}
                    />
                  </Grid>
                </Grid>
                {/* <TextField
                  fullWidth
                  autoComplete='mob'
                  label={'Mobile Number'}
                  disabled={true}
                  value={userMobileNumber}
                /> */}

                <TextField
                  fullWidth
                  label={'OTP'}
                  type='number'
                  value={showOTP}
                  onChange={(event) => handleInput(event.target.value)}
                  error={showOTP ? !isOtpValid(showOTP) : false}
                  helperText={
                    showOTP
                      ? !isOtpValid(showOTP)
                        ? 'OTP is incorrect'
                        : ''
                      : ''
                  }
                />
              </Stack>

              <Stack
                direction='column'
                alignItems='center'
                justifyContent='center'
                sx={{ my: 2 }}
                spacing={1}
              >
                <Typography
                  variant='subtitle2'
                  align='center'
                  color={'#637381'}
                  sx={{ fontSize: '0.99rem' }}
                >
                  If you didn't receive OTP! {timer !== '00:00' ? 'in ' + timer : ''}
                </Typography>
                {timer === '00:00' && (
                  <Stack
                    direction='row'
                    spacing={2}
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant='outlined'
                      onClick={() => resendCode('whatsapp')}
                      disabled={timer !== '00:00'}
                      size='small'
                    >
                      Resend via WhatsApp
                    </Button>
                    <Button
                      variant='outlined'
                      onClick={() => resendCode('sms')}
                      disabled={timer !== '00:00'}
                      size='small'
                    >
                      Resend via SMS
                    </Button>
                  </Stack>
                )}
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                justifyContent='center'
              >
                <LoadingButton
                  fullWidth
                  size='large'
                  type='submit'
                  variant='contained'
                  loading={isSubmitting}
                >
                  Verify
                </LoadingButton>
              </Stack>
            </form>
            <Typography
              variant='body1'
              align='center'
              sx={{
                mt: 3,
              }}
            >
              Back To&nbsp;
              <Link
                variant='subtitle1'
                component={RouterLink}
                to='/login'
                underline='hover'
              >
                Login
              </Link>
            </Typography>
          </Card>
        </ContentStyle>
      </Container>
    </RootStyle>
  );
};
export default Index;
